"use client";

import { useCallback, useId, useMemo, useState } from "react";
import { SalesSKUInvoice } from "tm-shared/types";

import BIcon from "@/assets/icons/sale-b.svg";
import LIcon from "@/assets/icons/sale-l.svg";
import MIcon from "@/assets/icons/sale-m.svg";
import FlexContainer from "@/components/FlexContainer";
import {
    StyledNestedAccordion,
    StyledNestedAccordionWrapper,
} from "@/components/NestedAccordion/Styled";
import { PrimaryText } from "@/components/Typography/Styled";
import { useAppSelector } from "@/hooks/redux";

import { EmptySalesResult } from "../EmptySalesResult";
import { InvoiceSKUInfoModal } from "./InvoiceSKUInfoModal";
import {
    StyledSBSKSum,
    StyledSBSKUInfoIcon,
    StyledSBSKURaw,
    StyledSBSKUSum,
} from "./Styled";

export const BySKU = () => {
    const { products } = useAppSelector((state) => state.offline);
    const { skuSales } = useAppSelector((state) => state.work);
    const invoiceSKUInfoModalKey = useId();
    const [modalInfo, setModalInfo] = useState<SalesSKUInvoice | null>(null);

    const isModalOpened = Boolean(modalInfo);

    const handleOpenModal = (invoice: SalesSKUInvoice) => {
        setModalInfo(invoice);
    };

    const handleCloseModal = useCallback(() => {
        setModalInfo(null);
    }, []);

    const accordionItems = useMemo(() => {
        return skuSales
            .map((skuItem) => {
                const currentProduct = products[skuItem.barcode];

                if (!currentProduct) {
                    return null;
                }
                const currentProductCapacity =
                    +currentProduct?.params?.capacity?.value;
                const totalSum = Object.values(skuItem.invoices).reduce(
                    (sum, inv) => sum + inv.countSKU * inv.price,
                    0,
                );
                const totalB = Object.values(skuItem.invoices).reduce(
                    (sum, inv) => sum + (inv.countSKU || 0),
                    0,
                );
                const totalL = (totalB * currentProductCapacity).toFixed(1);

                return {
                    key: skuItem.barcode,
                    label: (
                        <FlexContainer
                            gap="16px"
                            alignItems="flex-start"
                            justifyContent="space-between"
                        >
                            <FlexContainer flexDirection="column" gap="6px">
                                <PrimaryText fontWeight="400" fontSize={14}>
                                    {currentProduct?.name}
                                </PrimaryText>
                            </FlexContainer>

                            <FlexContainer
                                flexDirection="column"
                                alignItems="flex-end"
                                gap="4px"
                            >
                                <StyledSBSKUSum>
                                    {totalSum.toLocaleString()}₴
                                </StyledSBSKUSum>

                                <FlexContainer gap="6px" alignItems="center">
                                    <FlexContainer>
                                        <PrimaryText fontSize={12} $textNoWrap>
                                            {totalB}
                                        </PrimaryText>
                                        <img src={BIcon} alt="BIcon" />
                                    </FlexContainer>
                                    <FlexContainer>
                                        <PrimaryText fontSize={12} $textNoWrap>
                                            {totalL}
                                        </PrimaryText>
                                        <img src={LIcon} alt="LIcon" />
                                    </FlexContainer>
                                </FlexContainer>
                            </FlexContainer>
                        </FlexContainer>
                    ),
                    children: (
                        <FlexContainer
                            width="100%"
                            padding="0 0 0 24px"
                            flexDirection="column"
                            gap="12px"
                        >
                            {Object.values(skuItem.invoices).map((invoice) => (
                                <StyledSBSKURaw key={invoice.invoiceId}>
                                    <FlexContainer
                                        justifyContent="space-between"
                                        width="100%"
                                        alignItems="flex-start"
                                    >
                                        <FlexContainer gap="4px">
                                            <StyledSBSKUInfoIcon
                                                onClick={() =>
                                                    handleOpenModal(invoice)
                                                }
                                            />
                                            <PrimaryText fontSize={14}>
                                                {invoice.invoiceId}
                                            </PrimaryText>
                                        </FlexContainer>

                                        <StyledSBSKSum>
                                            {(
                                                invoice.countSKU * invoice.price
                                            ).toLocaleString()}
                                            ₴
                                        </StyledSBSKSum>
                                    </FlexContainer>

                                    <FlexContainer
                                        justifyContent="space-between"
                                        width="100%"
                                    >
                                        <FlexContainer>
                                            <PrimaryText fontSize={12}>
                                                {invoice.price.toFixed(2)}₴
                                            </PrimaryText>
                                            <img src={MIcon} alt="MIcon" />
                                        </FlexContainer>
                                        <FlexContainer>
                                            <PrimaryText fontSize={12}>
                                                {invoice.countSKU}
                                            </PrimaryText>
                                            <img src={BIcon} alt="BIcon" />
                                        </FlexContainer>
                                        <FlexContainer>
                                            <PrimaryText fontSize={12}>
                                                {(
                                                    invoice.countSKU *
                                                    currentProductCapacity
                                                ).toFixed(1)}
                                            </PrimaryText>
                                            <img src={LIcon} alt="LIcon" />
                                        </FlexContainer>
                                    </FlexContainer>
                                </StyledSBSKURaw>
                            ))}
                        </FlexContainer>
                    ),
                };
            })
            .filter(Boolean);
    }, [skuSales]);

    if (!skuSales.length) return <EmptySalesResult />;

    return (
        <>
            <InvoiceSKUInfoModal
                modalKey={invoiceSKUInfoModalKey}
                isOpen={isModalOpened}
                onClose={handleCloseModal}
                invoice={modalInfo}
            />

            <StyledNestedAccordionWrapper>
                <StyledNestedAccordion
                    accordion
                    level={0}
                    items={accordionItems}
                />
            </StyledNestedAccordionWrapper>
        </>
    );
};
